// ============================================================
//  Types & Constants — ExhibitorsManager Page Builder (Craft.js)
// ============================================================

// Legacy types for backward compatibility
export type PageId = string; // Now dynamic instead of fixed union
export type ZoneId = 'hero' | 'intro' | 'listing' | 'sidebar' | 'footer';
export type BlockType =
  | 'titre'
  | 'texte'
  | 'image'
  | 'bouton'
  | 'separateur'
  | 'espaceur'
  | 'section'
  | 'cta'
  | 'libre'
  | 'formulaire'
  | 'pricing'
  | 'offre'
  | 'validation'
  | 'form-input'
  | 'form-textarea'
  | 'form-radio'
  | 'form-checkbox'
  | 'form-select'
  | 'form-container';

export interface FormField {
  label: string;
  type: 'text' | 'email' | 'textarea' | 'radio' | 'checkbox' | 'select';
  options?: string[];
}

export interface PricingLine {
  label: string;
  price: string;
}
export interface ValidationLine {
  label: string;
  val: string;
}

export interface HeaderState {
  logoType: 'text' | 'image';
  logoText: string;
  logoImageSrc: string;
  bgColor: string;
  bgImageSrc: string;
  textColor: string;
  accentColor: string;
  showCta: boolean;
  ctaLabel: string;
  ctaUrl: string;
  height: string;
  shadow: boolean;
  // Typography settings
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  textAlign: 'left' | 'center' | 'right';
  // Image display options
  logoObjectFit: 'contain' | 'cover' | 'fill' | 'scale-down';
  bgSize: 'cover' | 'contain' | 'auto' | '100%';
  bgPosition: 'center' | 'top' | 'bottom' | 'left' | 'right';
  bgRepeat: 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y';
}

export interface CatalogEntry {
  group: string;
  type: BlockType;
  name: string;
  desc: string;
  icon: string;
  metier: boolean;
}

// ── Constants ─────────────────────────────────────────────
// Legacy constants - now dynamically managed
export const PAGES: PageId[] = ['accueil', 'offres', 'validation']; // Fallback for compatibility

export const PAGE_LABELS: Record<string, string> = {
  accueil: 'Accueil',
  offres: 'Offres',
  validation: 'Validation',
};

export const ZONE_LABELS: Record<ZoneId, string> = {
  hero: 'Hero',
  intro: 'Intro',
  listing: 'Listing offres',
  sidebar: 'Sidebar résumé',
  footer: 'Footer',
};

export const DEFAULT_ZONES: Record<PageId, ZoneId[]> = {
  accueil: ['hero', 'intro', 'footer'],
  offres: ['hero', 'listing', 'sidebar', 'footer'],
  validation: ['hero', 'intro', 'footer'],
};

export const BLOCK_CATALOG: CatalogEntry[] = [
  {
    group: 'Contenu',
    type: 'titre',
    name: 'Titre',
    desc: 'H1 à H4',
    icon: '📝',
    metier: false,
  },
  {
    group: 'Contenu',
    type: 'texte',
    name: 'Texte',
    desc: 'Paragraphe',
    icon: '¶',
    metier: false,
  },
  {
    group: 'Contenu',
    type: 'image',
    name: 'Image',
    desc: 'Photo ou visuel',
    icon: '🖼',
    metier: false,
  },
  {
    group: 'Contenu',
    type: 'bouton',
    name: 'Bouton',
    desc: 'CTA ou lien',
    icon: '⬛',
    metier: false,
  },
  {
    group: 'Contenu',
    type: 'separateur',
    name: 'Séparateur',
    desc: 'Ligne horizontale',
    icon: '—',
    metier: false,
  },
  {
    group: 'Contenu',
    type: 'espaceur',
    name: 'Espaceur',
    desc: 'Espace vertical',
    icon: '↕',
    metier: false,
  },
  {
    group: 'Mise en page',
    type: 'section',
    name: 'Section',
    desc: 'Conteneur coloré',
    icon: '⬚',
    metier: false,
  },
  // {
  //   group: 'Mise en page',
  //   type: 'cta',
  //   name: 'Bloc CTA',
  //   desc: "Appel à l'action",
  //   icon: '🎯',
  //   metier: false,
  // },
  // {
  //   group: 'Mise en page',
  //   type: 'libre',
  //   name: 'Bloc libre',
  //   desc: 'HTML personnalisé',
  //   icon: '</>',
  //   metier: false,
  // },
  // {
  //   group: 'Formulaires',
  //   type: 'formulaire',
  //   name: 'Formulaire',
  //   desc: 'Champs de contact',
  //   icon: '📋',
  //   metier: false,
  // },
  // {
  //   group: 'Formulaires',
  //   type: 'pricing',
  //   name: 'Résumé prix',
  //   desc: 'Tableau tarifaire',
  //   icon: '💶',
  //   metier: false,
  // },
  {
    group: 'Métier',
    type: 'offre',
    name: 'Carte offre',
    desc: 'Offre',
    icon: '🏢',
    metier: true,
  },
  {
    group: 'Métier',
    type: 'validation',
    name: 'Validation',
    desc: 'Récap & validation',
    icon: '✅',
    metier: true,
  },
  {
    group: 'Formulaires',
    type: 'form-input',
    name: 'Champ texte',
    desc: 'Saisie texte courte',
    icon: '✏',
    metier: false,
  },
  {
    group: 'Formulaires',
    type: 'form-textarea',
    name: 'Zone de texte',
    desc: 'Saisie texte longue',
    icon: '📄',
    metier: false,
  },
  {
    group: 'Formulaires',
    type: 'form-radio',
    name: 'Bouton radio',
    desc: 'Choix unique',
    icon: '⊙',
    metier: false,
  },
  {
    group: 'Formulaires',
    type: 'form-checkbox',
    name: 'Case à cocher',
    desc: 'Choix multiple',
    icon: '☑',
    metier: false,
  },
  {
    group: 'Formulaires',
    type: 'form-select',
    name: 'Liste déroulante',
    desc: 'Sélection dans une liste',
    icon: '▼',
    metier: false,
  },
  {
    group: 'Formulaires',
    type: 'form-container',
    name: 'Conteneur formulaire',
    desc: 'Formulaire indépendant avec bouton soumettre',
    icon: '📋',
    metier: false,
  },
];

export const DEFAULT_HEADER: HeaderState = {
  logoType: 'text',
  logoText: 'ExhibitorsManager',
  logoImageSrc: '',
  bgColor: '#ffffff',
  bgImageSrc: '',
  textColor: '#1a1917',
  accentColor: '#2563eb',
  showCta: true,
  ctaLabel: 'Espace exposant',
  ctaUrl: '#',
  height: '60',
  shadow: true,
  // Typography defaults
  fontFamily: 'Roboto',
  fontSize: '28',
  fontWeight: '600',
  textAlign: 'left',
  // Image display defaults
  logoObjectFit: 'contain',
  bgSize: 'cover',
  bgPosition: 'center',
  bgRepeat: 'no-repeat',
};
